/**
 * Selector de persona: el popover que abre una casilla de la rejilla.
 *
 * La casilla es «quién estuvo en esta sala a esta hora», así que lo que se elige es una
 * persona. Dos modos, porque una casilla admite dos cosas distintas (MVP §6.2):
 * **colocar** a alguien, o **descartarlo a lápiz**. En modo anotación el popover no se
 * cierra al elegir: lo normal es descartar a varios seguidos.
 *
 * Va en `position: fixed` anclado a la casilla en vez de dentro de ella porque la
 * rejilla tiene `overflow-x: auto` y recortaría cualquier hijo posicionado.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Eraser, Pencil, X } from 'lucide-react';

import { Portrait } from '../design/Portrait';
import type { PersonVisual } from './visuals';
import { matchByKey } from './visuals';

export type PickerMode = 'place' | 'note';

export interface PersonPickerProps {
  people: readonly PersonVisual[];
  /** Casilla a la que se ancla; también da el tamaño para no taparla. */
  anchor: HTMLElement | null;
  mode: PickerMode;
  onModeChange: (mode: PickerMode) => void;
  /** Persona colocada en la casilla, si la hay. */
  current: number | null;
  /** Personas descartadas a lápiz en la casilla. */
  notes: readonly number[];
  /** Descripción de la casilla para el lector de pantalla. */
  cellLabel: string;
  onPlace: (person: number) => void;
  onToggleNote: (person: number) => void;
  onClear: () => void;
  onClose: () => void;
}

const PANEL_WIDTH = 268;
const MARGIN = 8;

function positionFor(anchor: HTMLElement | null) {
  if (anchor === null) return { top: MARGIN, left: MARGIN };
  const rect = anchor.getBoundingClientRect();
  const viewportWidth = window.innerWidth || PANEL_WIDTH + MARGIN * 2;
  const viewportHeight = window.innerHeight || 0;

  const left = Math.max(
    MARGIN,
    Math.min(rect.left, viewportWidth - PANEL_WIDTH - MARGIN),
  );
  // Debajo de la casilla salvo que no quepa; entonces encima.
  const below = rect.bottom + MARGIN;
  const estimatedHeight = 340;
  const top =
    viewportHeight > 0 && below + estimatedHeight > viewportHeight
      ? Math.max(MARGIN, rect.top - estimatedHeight - MARGIN)
      : below;
  return { top, left };
}

export function PersonPicker({
  people,
  anchor,
  mode,
  onModeChange,
  current,
  notes,
  cellLabel,
  onPlace,
  onToggleNote,
  onClear,
  onClose,
}: PersonPickerProps) {
  const optionsRef = useRef<Array<HTMLButtonElement | null>>([]);
  const [position, setPosition] = useState(() => positionFor(anchor));
  const [active, setActive] = useState(() => (current === null ? 0 : current));

  useLayoutEffect(() => {
    setPosition(positionFor(anchor));
  }, [anchor]);

  useEffect(() => {
    optionsRef.current[active]?.focus();
    // Solo al abrir: mover el foco en cada cambio de `active` lo hace la navegación.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function focusOption(index: number) {
    const next = (index + people.length) % people.length;
    setActive(next);
    optionsRef.current[next]?.focus();
  }

  function choose(person: number) {
    if (mode === 'note') {
      onToggleNote(person);
      return;
    }
    onPlace(person);
    onClose();
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault();
      focusOption(active + 1);
      return;
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      event.preventDefault();
      focusOption(active - 1);
      return;
    }
    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      focusOption(event.key === 'Home' ? 0 : people.length - 1);
      return;
    }
    const person = matchByKey(
      people.map((one) => one.name),
      event.key,
      active,
    );
    if (person !== null) {
      event.preventDefault();
      setActive(person);
      choose(person);
    }
  }

  return (
    <>
      {/* Capa de cierre: un clic fuera cancela sin elegir nada. */}
      <div className="fixed inset-0 z-40" onPointerDown={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-label={`Quién estuvo en ${cellLabel}`}
        onKeyDown={onKeyDown}
        style={{ top: position.top, left: position.left, width: PANEL_WIDTH }}
        className="bg-surface-raised border-border shadow-hard fixed z-50 max-w-[calc(100vw-16px)] rounded-lg border p-2"
      >
        <div className="mb-2 flex items-center gap-1">
          <div
            role="radiogroup"
            aria-label="Modo del selector"
            className="flex flex-1 gap-1"
          >
            <ModeButton
              active={mode === 'place'}
              label="Colocar"
              onClick={() => onModeChange('place')}
            />
            <ModeButton
              active={mode === 'note'}
              label="Descartar"
              icon={<Pencil size={14} aria-hidden="true" />}
              onClick={() => onModeChange('note')}
            />
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar selector"
            className="text-text-muted hover:text-text focus-ring grid h-11 w-11 place-items-center rounded-md"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <ul className="flex flex-col gap-1">
          {people.map((person, index) => {
            const isPlaced = current === index;
            const isNoted = notes.includes(index);
            return (
              <li key={person.name}>
                <button
                  type="button"
                  ref={(element) => {
                    optionsRef.current[index] = element;
                  }}
                  tabIndex={active === index ? 0 : -1}
                  aria-pressed={mode === 'note' ? isNoted : isPlaced}
                  onFocus={() => setActive(index)}
                  onClick={() => choose(index)}
                  className={`focus-ring flex min-h-11 w-full items-center gap-2 rounded-md px-2 text-left text-sm ${
                    isPlaced ? 'ring-accent ring-2' : ''
                  } ${isNoted ? 'opacity-60' : ''}`}
                >
                  <Portrait portrait={person.portrait} size="sm" />
                  <span className={`flex-1 ${isNoted ? 'line-through' : ''}`}>
                    {person.name}
                  </span>
                  <kbd className="text-text-muted font-mono text-xs">
                    {person.shortcut}
                  </kbd>
                </button>
              </li>
            );
          })}
        </ul>

        {current !== null && (
          <button
            type="button"
            onClick={() => {
              onClear();
              onClose();
            }}
            className="focus-ring text-text-muted hover:text-text mt-2 flex min-h-11 w-full items-center gap-2 rounded-md px-2 text-sm"
          >
            <Eraser size={16} aria-hidden="true" />
            Vaciar la casilla
          </button>
        )}
      </div>
    </>
  );
}

function ModeButton({
  active,
  label,
  icon,
  onClick,
}: {
  active: boolean;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      className={`focus-ring flex min-h-11 flex-1 items-center justify-center gap-1.5 rounded-md border px-2 text-sm ${
        active
          ? 'border-accent bg-accent text-accent-contrast'
          : 'border-border text-text-muted'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}
